/**
 * Date windows for the Intelligence dashboards.
 *
 * The KPI page reads one trailing or calendar window and compares it with
 * the equivalent prior window so every tile can show a delta. The Forecast
 * page instead picks how much history to show and how far ahead to project.
 * Both are URL-synced by the page so a pasted link reproduces the view.
 */

import { addUtcDays, isoDaysAgo, type AnalyticsWindow } from '@/features/marketing/range';
import type { TimeGrain } from './types';

export type KpiRange = '7d' | '30d' | '90d' | 'mtd' | 'qtd' | 'ytd' | '12m';

export const KPI_RANGE_OPTIONS: Array<{ id: KpiRange; label: string; shortLabel: string }> = [
  { id: '7d', label: 'Last 7 days', shortLabel: '7d' },
  { id: '30d', label: 'Last 30 days', shortLabel: '30d' },
  { id: '90d', label: 'Last 90 days', shortLabel: '90d' },
  { id: 'mtd', label: 'Month to date', shortLabel: 'MTD' },
  { id: 'qtd', label: 'Quarter to date', shortLabel: 'QTD' },
  { id: 'ytd', label: 'Year to date', shortLabel: 'YTD' },
  { id: '12m', label: 'Trailing 12 months', shortLabel: '12m' },
];

const KPI_RANGE_IDS = new Set<string>(KPI_RANGE_OPTIONS.map((o) => o.id));

export function isKpiRange(value: string | null | undefined): value is KpiRange {
  return !!value && KPI_RANGE_IDS.has(value);
}

export interface DashboardWindow extends AnalyticsWindow {
  id: KpiRange;
  label: string;
  comparisonLabel: string;
  /** Inclusive day count of the current window. */
  days: number;
  /** Natural grain for charts over this window. */
  grain: TimeGrain;
  /** Earliest day any day-grain dataset needs to fetch (covers both windows). */
  fetchStart: string;
}

function utcParts(date = new Date()) {
  return { year: date.getUTCFullYear(), month: date.getUTCMonth(), day: date.getUTCDate() };
}

function isoFromUtc(year: number, monthIndex: number, day: number): string {
  return new Date(Date.UTC(year, monthIndex, day)).toISOString().slice(0, 10);
}

function clampUtcDate(year: number, monthIndex: number, day: number): string {
  const lastDay = new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  return isoFromUtc(year, monthIndex, Math.min(day, lastDay));
}

export function daysBetweenInclusive(start: string, end: string): number {
  const a = Date.parse(`${start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}

export function resolveKpiWindow(range: KpiRange): DashboardWindow {
  const today = isoDaysAgo(0);
  const { year, month, day } = utcParts();
  const option = KPI_RANGE_OPTIONS.find((o) => o.id === range)!;

  let win: AnalyticsWindow;
  let comparisonLabel: string;
  switch (range) {
    case '7d':
    case '30d':
    case '90d': {
      const length = Number.parseInt(range, 10);
      const currentStart = addUtcDays(today, -(length - 1));
      const previousEnd = addUtcDays(currentStart, -1);
      const previousStart = addUtcDays(previousEnd, -(length - 1));
      win = { currentStart, currentEnd: today, previousStart, previousEnd };
      comparisonLabel = `vs prior ${length} days`;
      break;
    }
    case 'mtd': {
      const currentStart = isoFromUtc(year, month, 1);
      const prevMonthEnd = addUtcDays(currentStart, -1);
      const prev = utcParts(new Date(`${prevMonthEnd}T00:00:00Z`));
      win = {
        currentStart,
        currentEnd: today,
        previousStart: isoFromUtc(prev.year, prev.month, 1),
        previousEnd: clampUtcDate(prev.year, prev.month, day),
      };
      comparisonLabel = 'vs last month to date';
      break;
    }
    case 'qtd': {
      const quarterStartMonth = month - (month % 3);
      const currentStart = isoFromUtc(year, quarterStartMonth, 1);
      const elapsed = daysBetweenInclusive(currentStart, today);
      const prevQuarterStart = new Date(Date.UTC(year, quarterStartMonth - 3, 1));
      const previousStart = prevQuarterStart.toISOString().slice(0, 10);
      win = {
        currentStart,
        currentEnd: today,
        previousStart,
        previousEnd: addUtcDays(previousStart, elapsed - 1),
      };
      comparisonLabel = 'vs last quarter to date';
      break;
    }
    case 'ytd': {
      const currentStart = isoFromUtc(year, 0, 1);
      win = {
        currentStart,
        currentEnd: today,
        previousStart: isoFromUtc(year - 1, 0, 1),
        previousEnd: clampUtcDate(year - 1, month, day),
      };
      comparisonLabel = 'vs last year to date';
      break;
    }
    case '12m': {
      const currentStart = isoFromUtc(year - 1, month + 1, 1);
      const previousEnd = addUtcDays(currentStart, -1);
      const previousStart = isoFromUtc(year - 2, month + 1, 1);
      win = { currentStart, currentEnd: today, previousStart, previousEnd };
      comparisonLabel = 'vs prior 12 months';
      break;
    }
  }

  const days = daysBetweenInclusive(win.currentStart, win.currentEnd);
  const grain: TimeGrain = days <= 45 ? 'day' : days <= 200 ? 'week' : 'month';
  return {
    id: range,
    label: option.label,
    comparisonLabel,
    days,
    grain,
    fetchStart: win.previousStart < win.currentStart ? win.previousStart : win.currentStart,
    ...win,
  };
}

// ---------------------------------------------------------------------------
// Forecast page settings
// ---------------------------------------------------------------------------

export type ForecastHorizon = 3 | 6 | 12;
export type ForecastHistory = 6 | 12 | 24;

export const FORECAST_HORIZON_OPTIONS: Array<{ id: ForecastHorizon; label: string }> = [
  { id: 3, label: 'Next 3 months' },
  { id: 6, label: 'Next 6 months' },
  { id: 12, label: 'Next 12 months' },
];

export const FORECAST_HISTORY_OPTIONS: Array<{ id: ForecastHistory; label: string }> = [
  { id: 6, label: '6 months back' },
  { id: 12, label: '12 months back' },
  { id: 24, label: '24 months back' },
];

export function parseForecastHorizon(value: string | null | undefined): ForecastHorizon {
  const n = Number(value);
  return n === 3 || n === 6 || n === 12 ? n : 6;
}

export function parseForecastHistory(value: string | null | undefined): ForecastHistory {
  const n = Number(value);
  return n === 6 || n === 12 || n === 24 ? n : 12;
}

/** Window a forecast dashboard needs to fetch: `history` months back through today. */
export function resolveForecastWindow(history: ForecastHistory): DashboardWindow {
  const today = isoDaysAgo(0);
  const { year, month } = utcParts();
  // One extra month so partial-month smoothing has a full period to lean on.
  const currentStart = isoFromUtc(year, month - history, 1);
  const previousEnd = addUtcDays(currentStart, -1);
  const previousStart = isoFromUtc(year, month - history * 2, 1);
  return {
    id: '12m',
    label: `${history} months of history`,
    comparisonLabel: 'vs prior period',
    days: daysBetweenInclusive(currentStart, today),
    grain: 'month',
    fetchStart: previousStart,
    currentStart,
    currentEnd: today,
    previousStart,
    previousEnd,
  };
}
